import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DASH } from "@/constants/testIds";
import { toast } from "sonner";

const P_ORDER = ["Sun","Moon","Mars","Mercury","Jupiter","Venus","Saturn","Rahu","Ketu"];

function md(text) {
  const lines = text.split("\n");
  return lines.map((l, i) => {
    const k = `L${i}-${l.slice(0, 24)}`;
    if (l.startsWith("## ")) return <h3 key={k} className="font-heading text-xl text-[#D4AF37] mt-6 mb-2">{l.slice(3)}</h3>;
    if (l.startsWith("# ")) return <h2 key={k} className="font-heading text-2xl text-[#E6E1D6] mt-6 mb-2">{l.slice(2)}</h2>;
    if (l.startsWith("- ")) return <li key={k} className="text-[#E6E1D6] ml-6 list-disc">{fmtBold(l.slice(2))}</li>;
    if (l.startsWith("* ")) return <li key={k} className="text-[#E6E1D6] ml-6 list-disc">{fmtBold(l.slice(2))}</li>;
    if (!l.trim()) return <div key={k} className="h-2" />;
    return <p key={k} className="text-[#E6E1D6] leading-relaxed font-light">{fmtBold(l)}</p>;
  });
}
function fmtBold(s) {
  return s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith("**") && p.endsWith("**")
    ? <strong key={`b${i}`} className="text-[#D4AF37] font-medium">{p.slice(2, -2)}</strong>
    : <span key={`s${i}`}>{p}</span>);
}

const addDays = (dateStr, days) => {
  const d = new Date(dateStr);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const addMonths = (dateStr, months) => {
  const d = new Date(dateStr);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
};

export default function PredictionPanel({ profileId }) {
  const [rangeType, setRangeType] = useState("day");
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [mdIndex, setMdIndex] = useState(0);
  const [mdList, setMdList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.dasha(profileId);
        setMdList(r.dasha || []);
      } catch (err) { console.error("dasha fetch failed:", err); }
    })();
  }, [profileId]);

  const fetch = async () => {
    setLoading(true); setData(null);
    try {
      if (rangeType === "day") {
        const r = await api.day(profileId, date);
        setData({ kind: "day", ...r });
      } else if (rangeType === "week") {
        const end = addDays(date, 6);
        const r = await api.range(profileId, "weekly", date, end);
        setData({ kind: "week", ...r });
      } else if (rangeType === "month") {
        const end = addDays(addMonths(date, 1), -1);
        const r = await api.range(profileId, "monthly", date, end);
        setData({ kind: "month", ...r });
      } else if (rangeType === "year") {
        const y = new Date(date).getUTCFullYear();
        const r = await api.year(profileId, y);
        setData({ kind: "year", ...r, prediction: r.prediction, year: y });
      } else if (rangeType === "mahadasha") {
        const r = await api.mahadasha(profileId, mdIndex);
        setData({ kind: "mahadasha", ...r });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Prediction failed"); }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#A19E95] font-mono mb-2">Range</div>
          <Select value={rangeType} onValueChange={setRangeType}>
            <SelectTrigger data-testid="range-type-select"
                           className="w-40 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6]">
              <SelectItem value="day">Day</SelectItem>
              <SelectItem value="week">Week (7 days)</SelectItem>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="year">Year</SelectItem>
              <SelectItem value="mahadasha">Mahadasha</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {rangeType !== "mahadasha" && (
          <div>
            <div className="text-xs uppercase tracking-widest text-[#A19E95] font-mono mb-2">
              {rangeType === "day" ? "Date" : rangeType === "week" ? "Week starts" : "Month starts"}
            </div>
            <Input data-testid={DASH.daySelect} type="date" value={date}
                   onChange={e => setDate(e.target.value)}
                   className="bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6] font-mono w-48" />
          </div>
        )}

        {rangeType === "mahadasha" && mdList.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-widest text-[#A19E95] font-mono mb-2">Mahadasha</div>
            <Select value={String(mdIndex)} onValueChange={(v) => setMdIndex(Number(v))}>
              <SelectTrigger data-testid="mahadasha-index-select"
                             className="w-72 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6] max-h-64">
                {mdList.map((m, i) => (
                  <SelectItem key={`${m.lord}-${m.start}`} value={String(i)}>
                    {m.lord} · {m.start.slice(0, 10)} → {m.end.slice(0, 10)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Button data-testid={DASH.dayFetchBtn} onClick={fetch} disabled={loading}
                className="rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158] transition-colors duration-200">
          {loading ? "Reading…" : "Generate Prediction"}
        </Button>
      </div>

      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-[#2A2D35] bg-[#181A1F] p-6" data-testid="prediction-panel">
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono mb-2">
              {data.kind === "day" && `Day · ${data.date}`}
              {data.kind === "week" && `Week · ${data.start} → ${data.end}`}
              {data.kind === "month" && `Month · ${data.start} → ${data.end}`}
              {data.kind === "year" && `Year · ${data.year}`}
              {data.kind === "mahadasha" && `Mahadasha · ${data.md.lord} · ${data.md.start.slice(0,10)} → ${data.md.end.slice(0,10)}`}
            </div>
            <div className="space-y-1">{md(data.prediction)}</div>
          </div>

          <div className="space-y-4">
            {(data.active_dasha || []).length > 0 && (
              <div className="border border-[#2A2D35] bg-[#181A1F] p-4">
                <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono mb-3">Active Dasha</div>
                {data.active_dasha.map((c) => (
                  <div key={`L${c.level}-${c.lord}`} className="flex items-baseline justify-between text-sm py-1 border-b border-[#2A2D35]/50">
                    <span className="font-mono text-[10px] text-[#6E6B64]">L{c.level}</span>
                    <span className="font-heading text-[#E6E1D6]">{c.lord}</span>
                    <span className="font-mono text-xs text-[#A19E95]">{c.end.slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            )}

            {data.transits && (
              <div className="border border-[#2A2D35] bg-[#181A1F] p-4">
                <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono mb-3">Transits</div>
                {P_ORDER.map(p => (
                  <div key={p} className="flex items-baseline justify-between text-xs font-mono py-1 border-b border-[#2A2D35]/50">
                    <span className="text-[#E6E1D6]">{p}</span>
                    <span className="text-[#A19E95]">{data.transits[p].sign_name}</span>
                    <span className="text-[#6E6B64]">H{data.transits[p].house_from_lagna}</span>
                    <span className="text-[#C15C3D]">{data.transits[p].retrograde ? "R" : ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
